import { type Currency, EmployeeStatus, type Prisma } from "@prisma/client";
import { type AsyncReturnType } from "type-fest";
import { type AppContext } from "~/lib/context";
import { getRequiredUser } from "~/lib/getRequiredUser";
import { chain, mapValues, maxBy, sumBy } from "~/lib/lodash";
import { weightedMeanBy } from "~/lib/math";
import { convertCurrency } from "~/lib/money";
import { assertNotNil, hasNonNullableField } from "~/lib/utils";
import { type FetchComptryxMarketDataInput } from "~/pages/api/comptryx/fetch-comptryx-market-data";
import { ComptryxMeasure, formatComptryxMeasure } from "~/services/comptryx/comptryxMeasure";
import { type ComptryxJobForSelector, fetchComptryxJobsByIds } from "~/services/comptryx/fetchComptryxJobs";
import { fetchComptryxLevelsByIds } from "~/services/comptryx/fetchComptryxLevels";
import {
  type ComptryxLocationForSelector,
  fetchComptryxLocationsByIds,
} from "~/services/comptryx/fetchComptryxLocations";
import { getDefaultCurrency } from "~/services/currency";
import { combineDataQuality, computeComptryxDataQuality } from "~/services/dataQuality";
import { buildMarketDataEmployeesSelect } from "~/services/market-data/getMarketDataEmployees";

export const selectComptryxMarketDataPercentilesAmounts = {
  p10: true,
  p25: true,
  p50: true,
  p75: true,
  p90: true,
} satisfies Prisma.ComptryxMarketDataPercentilesSelect;

type ComptryxMarketDataPercentilesAmounts = Prisma.ComptryxMarketDataPercentilesGetPayload<{
  select: typeof selectComptryxMarketDataPercentilesAmounts;
}>;

export const fetchComptryxMarketData = async (ctx: AppContext, input: FetchComptryxMarketDataInput) => {
  const targetCurrency = await getDefaultCurrency(ctx);

  const [results, compareResults, externalEmployees, suggestedCurrencies] = await Promise.all([
    searchMarketDataResults(ctx, {
      jobIds: input.jobIds,
      levelIds: input.levelIds,
      locationIds: input.locationIds,
      filterId: input.filterId,
      targetCurrency,
    }),
    searchMarketDataResults(ctx, {
      jobIds: input.compareJobIds,
      levelIds: input.levelIds,
      locationIds: input.compareLocationIds,
      filterId: input.compareFilterId,
      targetCurrency,
    }),
    fetchExternalEmployees(ctx, {
      showCompanyEmployees: input.showCompanyEmployees,
      jobIds: [...input.jobIds, ...input.compareJobIds],
      levelIds: input.levelIds,
      locationIds: [...input.locationIds, ...input.compareLocationIds],
      targetCurrency,
    }),
    fetchSuggestedCurrencies(ctx, [...input.locationIds, ...input.compareLocationIds]),
  ]);

  const byLevel = await aggregateMarketDataResultsByLevel(ctx, {
    results,
    compareResults,
    externalEmployees,
    targetCurrency,
    input,
  });

  return {
    currency: targetCurrency,
    suggestedCurrencies,
    byLevel,
  };
};

export type FetchComptryxMarketDataResult = AsyncReturnType<typeof fetchComptryxMarketData>;

const searchMarketDataResults = async (
  ctx: AppContext,
  params: {
    jobIds: number[];
    levelIds: number[];
    locationIds: number[];
    filterId: number | null;
    targetCurrency: Currency;
  }
) => {
  if (params.jobIds.length === 0 || params.levelIds.length === 0 || params.locationIds.length === 0) {
    return null;
  }

  const results = await ctx.prisma.comptryxMarketDataResult.findMany({
    where: {
      jobId: { in: params.jobIds },
      levelId: { in: params.levelIds },
      locationId: { in: params.locationIds },
      filterId: params.filterId,
    },
    select: {
      jobId: true,
      levelId: true,
      locationId: true,
      employeesCount: true,
      companiesCount: true,
      baseSalary: { select: selectComptryxMarketDataPercentilesAmounts },
      onTargetTotalCash: { select: selectComptryxMarketDataPercentilesAmounts },
      actualTotalCash: { select: selectComptryxMarketDataPercentilesAmounts },
      currency: true,
    },
  });

  return results.map((result) => ({
    ...result,
    baseSalary: remapComptryxMarketDataPercentiles({
      percentiles: result.baseSalary,
      currency: result.currency,
      targetCurrency: params.targetCurrency,
    }),
    onTargetTotalCash: remapComptryxMarketDataPercentiles({
      percentiles: result.onTargetTotalCash,
      currency: result.currency,
      targetCurrency: params.targetCurrency,
    }),
    actualTotalCash: remapComptryxMarketDataPercentiles({
      percentiles: result.actualTotalCash,
      currency: result.currency,
      targetCurrency: params.targetCurrency,
    }),
  }));
};

type SearchMarketDataResults = AsyncReturnType<typeof searchMarketDataResults>;

export const remapComptryxMarketDataPercentiles = (params: {
  percentiles: ComptryxMarketDataPercentilesAmounts | null;
  currency: Currency;
  targetCurrency: Currency;
}) => {
  if (!params.percentiles) return null;

  const remapAmount = (amount: number) => ({
    amount: convertCurrency(amount, params.currency, params.targetCurrency),
    currency: params.targetCurrency,
  });

  type Output = {
    [key in keyof ComptryxMarketDataPercentilesAmounts]: ReturnType<typeof remapAmount>;
  };

  return mapValues(params.percentiles, remapAmount) as Output;
};

const aggregateMarketDataResultsByLevel = async (
  ctx: AppContext,
  params: {
    results: SearchMarketDataResults;
    compareResults: SearchMarketDataResults;
    externalEmployees: FetchExternalEmployeesResult;
    targetCurrency: Currency;
    input: FetchComptryxMarketDataInput;
  }
) => {
  const levels = await fetchComptryxLevelsByIds(ctx, params.input.levelIds);
  const jobs = await fetchComptryxJobsByIds(ctx, [...params.input.jobIds, ...params.input.compareJobIds]);
  const locations = await fetchComptryxLocationsByIds(ctx, [
    ...params.input.locationIds,
    ...params.input.compareLocationIds,
  ]);

  return chain(levels)
    .map((level) => {
      const resultsForLevel = params.results?.filter((result) => result.levelId === level.id) ?? [];

      const compareResultsForLevel = params.compareResults
        ? params.compareResults.filter((result) => result.levelId === level.id)
        : null;

      const externalEmployeesForLevel = params.externalEmployees.filter(
        (employee) => employee.comptryxLevelId === level.id
      );

      return {
        level,
        results: resultsForLevel,
        compareResults: compareResultsForLevel,
        externalEmployees: externalEmployeesForLevel,
      };
    })
    .map(({ level, results, compareResults, externalEmployees }) => {
      return {
        level,
        results: assertNotNil(
          processMarketDataResults(ctx, {
            results,
            targetCurrency: params.targetCurrency,
            jobs: jobs.filter((job) => params.input.jobIds.includes(job.id)),
            locations: locations.filter((location) => params.input.locationIds.includes(location.id)),
            externalEmployees,
          })
        ),
        compareResults: processMarketDataResults(ctx, {
          results: compareResults,
          targetCurrency: params.targetCurrency,
          jobs: jobs.filter((job) => params.input.compareJobIds.includes(job.id)),
          locations: locations.filter((location) => params.input.compareLocationIds.includes(location.id)),
          externalEmployees,
        }),
      };
    })
    .value();
};

const processMarketDataResults = (
  ctx: AppContext,
  params: {
    results: SearchMarketDataResults;
    targetCurrency: Currency;
    jobs: ComptryxJobForSelector[];
    locations: ComptryxLocationForSelector[];
    externalEmployees: FetchExternalEmployeesResult;
  }
) => {
  if (!params.results) return null;

  const measures = aggregateMarketDataResults({ results: params.results, targetCurrency: params.targetCurrency });
  const dataQuality = computeCombinedDataQuality(measures);
  const dataQualityDetailledByMeasure = computeDataQualityDetailledByMeasure(ctx, measures);
  const byJob = aggregateResultsByJob({ results: params.results, jobs: params.jobs });
  const byLocation = aggregateResultsByLocation({ results: params.results, locations: params.locations });
  const externalEmployees = params.externalEmployees.filter(
    (employee) =>
      params.jobs.some((job) => job.id === employee.comptryxJobId) &&
      params.locations.some((location) => location.id === employee.comptryxLocationId)
  );

  return {
    measures,
    dataQuality,
    dataQualityDetailledByMeasure,
    byJob,
    byLocation,
    externalEmployees,
  };
};

export const aggregateMarketDataResults = (params: {
  results: NonNullable<SearchMarketDataResults>;
  targetCurrency: Currency;
}) => {
  const aggregatePercentile = (
    unit: "baseSalary" | "onTargetTotalCash" | "actualTotalCash",
    percentile: keyof ComptryxMarketDataPercentilesAmounts
  ) => {
    const amount = weightedMeanBy(params.results, (result) => [
      result[unit]?.[percentile].amount ?? null,
      result.employeesCount,
    ]);

    return {
      currency: params.targetCurrency,
      amount: Math.round(amount),
    };
  };

  const aggregatePercentiles = (unit: "baseSalary" | "onTargetTotalCash" | "actualTotalCash") => {
    const relevantResults = params.results.filter((result) => !!result[unit]);

    if (!relevantResults.length) return null;

    const employeesCount = sumBy(relevantResults, (result) => result.employeesCount);
    const estimatedCompaniesCount = maxBy(relevantResults, (result) => result.companiesCount)?.companiesCount ?? 0;
    const dataQuality = computeComptryxDataQuality({ employeesCount, estimatedCompaniesCount });

    return {
      employeesCount,
      estimatedCompaniesCount,
      dataQuality,

      p10: aggregatePercentile(unit, "p10"),
      p25: aggregatePercentile(unit, "p25"),
      p50: aggregatePercentile(unit, "p50"),
      p75: aggregatePercentile(unit, "p75"),
      p90: aggregatePercentile(unit, "p90"),
    };
  };

  return {
    [ComptryxMeasure.BASE_SALARY]: aggregatePercentiles("baseSalary"),
    [ComptryxMeasure.ON_TARGET_TOTAL_CASH]: aggregatePercentiles("onTargetTotalCash"),
    [ComptryxMeasure.ACTUAL_TOTAL_CASH]: aggregatePercentiles("actualTotalCash"),
  };
};

const computeCombinedDataQuality = (measures: ReturnType<typeof aggregateMarketDataResults>) => {
  const dataQualities = chain(measures)
    .values()
    .compact()
    .map((measure) => measure.dataQuality)
    .value();

  if (dataQualities.length === 0) return null;

  return combineDataQuality(dataQualities);
};

const computeDataQualityDetailledByMeasure = (
  ctx: AppContext,
  measures: ReturnType<typeof aggregateMarketDataResults>
) => {
  return chain(ComptryxMeasure)
    .values()
    .map((measure) => {
      const measureData = measures[measure];

      if (!measureData) return null;

      return {
        measure: formatComptryxMeasure(ctx.t, measure as ComptryxMeasure),
        employeesCount: measureData.employeesCount,
        companiesCount: measureData.estimatedCompaniesCount,
        dataQuality: measureData.dataQuality,
      };
    })
    .compact()
    .value();
};

const aggregateResultsByJob = (params: { results: SearchMarketDataResults; jobs: ComptryxJobForSelector[] }) => {
  return chain(params.results)
    .groupBy((result) => result.jobId)
    .map((results) => {
      const job = assertNotNil(params.jobs.find((job) => job.id === results[0]?.jobId));

      return {
        job,
        employeesCount: sumBy(results, (result) => result.employeesCount),
      };
    })
    .orderBy((result) => result.employeesCount, "desc")
    .value();
};

const aggregateResultsByLocation = (params: {
  results: SearchMarketDataResults;
  locations: ComptryxLocationForSelector[];
}) => {
  return chain(params.results)
    .groupBy((result) => result.locationId)
    .map((results) => {
      const location = assertNotNil(params.locations.find((location) => location.id === results[0]?.locationId));

      return {
        location,
        employeesCount: sumBy(results, (result) => result.employeesCount),
      };
    })
    .orderBy((result) => result.employeesCount, "desc")
    .value();
};

const fetchExternalEmployees = async (
  ctx: AppContext,
  params: {
    showCompanyEmployees: boolean;
    jobIds: number[];
    levelIds: number[];
    locationIds: number[];
    targetCurrency: Currency;
  }
) => {
  if (!params.showCompanyEmployees || !ctx.globalPermissionsContext.canAccessYouVsMarket) return [];

  const externalEmployees = await ctx.prisma.externalEmployee.findMany({
    where: {
      companyId: getRequiredUser(ctx).companyId,
      comptryxJobId: { in: params.jobIds },
      comptryxLevelId: { in: params.levelIds },
      comptryxLocationId: { in: params.locationIds },

      mappedEmployee: {
        status: EmployeeStatus.LIVE,
      },
    },
    select: {
      comptryxJobId: true,
      comptryxLevelId: true,
      comptryxLocationId: true,
      mappedEmployee: {
        select: buildMarketDataEmployeesSelect(ctx),
      },
    },
  });

  return externalEmployees.filter((externalEmployee) => hasNonNullableField(externalEmployee, "mappedEmployee"));
};

type FetchExternalEmployeesResult = AsyncReturnType<typeof fetchExternalEmployees>;

const fetchSuggestedCurrencies = async (ctx: AppContext, locationIds: number[]) => {
  const [defaultCurrency, locations] = await Promise.all([
    getDefaultCurrency(ctx),
    ctx.prisma.comptryxLocation.findMany({
      where: { id: { in: locationIds } },
      select: {
        country: {
          select: {
            defaultCurrency: true,
          },
        },
      },
    }),
  ]);

  return chain(locations)
    .map((location) => location.country.defaultCurrency)
    .push(defaultCurrency)
    .compact()
    .uniqBy((currency) => currency.id)
    .value();
};
